/**
 * RoofDiagramAnnotator — Fold editors (Iter230b — extracted)
 * ───────────────────────────────────────────────────────────
 * `ValleyFoldEditor` (per-pin fold chip editor + save-as-template
 * prompt) and `FoldCrossSectionPreview` (SVG side-profile renderer).
 *
 * These were named exports from RoofDiagramAnnotator.js until
 * Iter230b — extracted to their own module so the annotator's
 * primary file is smaller and so other components
 * (PenetrationEditPanel, future Pipeline blocks) can import them
 * without dragging the entire annotator's import graph.
 *
 * RoofDiagramAnnotator.js re-exports both so existing import paths
 * (`import { ValleyFoldEditor } from './RoofDiagramAnnotator'`)
 * continue to work without changes.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save } from 'lucide-react';

/**
 * ValleyFoldEditor — Iter229c
 *
 * Compact horizontal per-pin fold editor for Valley pins. Mirrors the
 * Components-Flashings inline editor (numbered pill chips + + Fold +
 * Σ total). Operator can edit ANY fold's length, add new folds, remove
 * folds, OR reset to the template default. Edits are stored as a full
 * folds array on `pin.linked_flashing_folds` (per-pin only — never
 * mutates the master template). Absence of this field means "use the
 * template's folds as-is".
 *
 * "💾 Save as new template" snapshots the current effective folds into
 * a new flashing_templates doc (category='Valley', is_seed=false) so
 * future pins can pick it from the cascade.
 */
export function ValleyFoldEditor({ api, pin, setPin, linkedTpl }) {
  const tplFolds = linkedTpl?.folds || [];
  const [savingTpl, setSavingTpl] = useState(false);
  const [savePromptOpen, setSavePromptOpen] = useState(false);
  const [newTplName, setNewTplName] = useState('');
  // Iter229g — per-template preview rotation. Stored on the template
  // doc (`preview_rotation_deg`) so every pin sharing this template
  // sees the same orientation. Local state mirrors the saved value
  // while a PATCH is in flight; falls back to the template's value.
  const [previewRot, setPreviewRot] = useState(Number(linkedTpl?.preview_rotation_deg) || 0);
  useEffect(() => {
    setPreviewRot(Number(linkedTpl?.preview_rotation_deg) || 0);
  }, [linkedTpl?._id, linkedTpl?.preview_rotation_deg]);

  const onRotate = async (deg) => {
    setPreviewRot(deg);
    if (!api || !linkedTpl?._id) return;
    try {
      await api.patch(`/flashing-templates/${linkedTpl._id}`, { preview_rotation_deg: deg });
    } catch (e) {
      // Non-fatal — rotation works locally even if save fails.
      console.warn('Could not persist preview_rotation_deg', e);
    }
  };

  // Iter229d — Barge pins carry a Starter/Finish role:
  //   • Starter: folds locked to template defaults (standard sheet cover
  //     width). Orderable upfront with the initial sheets.
  //   • Finish:  folds editable per-pin (operator measures the final
  //     width on-site after sheets installed). Order placement is
  //     gated until measurement is taken.
  // Default role for fresh Barge pins is 'starter' (most common).
  const isBarge = pin.type === 'Barge';
  const role = pin.barge_role || (isBarge ? 'starter' : null);
  const isFinish = isBarge && role === 'finish';
  // Starter barges shouldn't allow fold edits — they're locked to the
  // template's standard cover width. Finish barges unlock for custom
  // measurement. Valley pins (non-Barge) are always editable.
  const foldsLocked = isBarge && role === 'starter';

  // Effective folds = the operator's per-pin array OR the template's.
  const effFolds = Array.isArray(pin.linked_flashing_folds) && pin.linked_flashing_folds.length
    ? pin.linked_flashing_folds
    : tplFolds;

  // Helper: clone the effective folds into a writable copy. Used as the
  // starting point for any edit (init from template the first time).
  const cloneFolds = () => effFolds.map((f) => ({
    length_mm: Number(f.length_mm) || 0,
    bend_angle: Number(f.bend_angle) || 0,
    bend_dir: f.bend_dir || 'ccw',
  }));

  const setFolds = (folds) => {
    // If the new folds exactly match the template, clear the override
    // so the pin falls back to the template (no stale data).
    const sameLength = folds.length === tplFolds.length;
    const matches = sameLength && folds.every((f, i) =>
      Number(f.length_mm) === Number(tplFolds[i]?.length_mm) &&
      Number(f.bend_angle) === Number(tplFolds[i]?.bend_angle)
    );
    setPin({
      ...pin,
      linked_flashing_folds: matches ? undefined : folds,
      dimensions_confirmed: true,
    });
  };

  const updateFoldLen = (idx, value) => {
    const next = cloneFolds();
    next[idx] = { ...next[idx], length_mm: Math.max(0, parseInt(value, 10) || 0) };
    setFolds(next);
  };
  const addFold = () => {
    const next = cloneFolds();
    next.push({ length_mm: 50, bend_angle: 90, bend_dir: 'ccw' });
    setFolds(next);
  };
  const removeFold = (idx) => {
    if (effFolds.length <= 1) return;
    const next = cloneFolds();
    next.splice(idx, 1);
    setFolds(next);
  };
  const resetAll = () => {
    setPin({ ...pin, linked_flashing_folds: undefined, dimensions_confirmed: true });
  };

  const girth = effFolds.reduce((s, f) => s + (Number(f.length_mm) || 0), 0);
  const tplGirth = tplFolds.reduce((s, f) => s + (Number(f.length_mm) || 0), 0);
  const girthDrift = girth !== tplGirth || effFolds.length !== tplFolds.length;
  const summary = effFolds.map((f) => Math.round(Number(f.length_mm) || 0)).join('/');

  const saveAsNewTemplate = async () => {
    const name = (newTplName || '').trim();
    if (!name) {
      toast.error('Pick a name for the new valley template');
      return;
    }
    if (!api) return;
    setSavingTpl(true);
    try {
      const folds = effFolds.map((f) => ({
        length_mm: Number(f.length_mm) || 0,
        bend_angle: Number(f.bend_angle) || 0,
        bend_dir: f.bend_dir || 'ccw',
      }));
      const payload = {
        name,
        category: linkedTpl.category || 'Valley',
        sheet_profile: linkedTpl.sheet_profile || '',
        sheet_profiles: linkedTpl.sheet_profiles || [],
        start_angle_deg: linkedTpl.start_angle_deg || 0,
        folds,
        label_offsets: linkedTpl.label_offsets || [],
        pipe_usage_links: linkedTpl.pipe_usage_links || [],
        edges: linkedTpl.edges || { start: null, end: null },
        edge_configs: linkedTpl.edge_configs || { start: {}, end: {} },
        primary_editable_folds: linkedTpl.primary_editable_folds || [],
        applies_to_all_profiles: linkedTpl.applies_to_all_profiles || false,
        notes: `Custom valley derived from "${linkedTpl.name}" with dimensions ${summary}`,
      };
      const r = await api.post('/flashing-templates', payload);
      const saved = r?.data;
      if (saved?._id) {
        setPin({
          ...pin,
          linked_flashing_template_id: saved._id,
          linked_flashing_name: saved.name,
          linked_flashing_folds: undefined,        // baked into new template
          linked_flashing_fold_overrides: {},
          dimensions_confirmed: true,
        });
        toast.success(`Saved "${saved.name}" as a new Valley template`);
        setSavePromptOpen(false);
        setNewTplName('');
      } else {
        toast.error('Template save returned no id');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      toast.error(`Could not save template: ${detail}`);
    } finally {
      setSavingTpl(false);
    }
  };

  return (
    <div
      className="bg-white rounded ring-1 ring-emerald-300 px-1.5 py-1.5 space-y-1.5"
      data-testid="annot-edit-valley-fold-editor"
    >
      {/* Iter229d — Barge-only Starter/Finish role toggle. Starter
          locks folds to template (standard sheet cover width); Finish
          unlocks for custom measurement after sheets installed. */}
      {isBarge && (
        <div
          className="flex items-center justify-between gap-1 text-[10px]"
          data-testid="annot-edit-barge-role-toggle"
        >
          <span className="font-bold uppercase tracking-[0.18em] text-emerald-700">Role</span>
          <div className="inline-flex rounded ring-1 ring-emerald-300 overflow-hidden">
            <button
              type="button"
              onClick={() => setPin({
                ...pin,
                barge_role: 'starter',
                // Snap folds back to the template when switching to
                // Starter — starter barges are by definition locked.
                linked_flashing_folds: undefined,
              })}
              data-testid="annot-edit-barge-role-starter"
              className={`px-2 py-0.5 font-semibold ${role === 'starter'
                ? 'bg-emerald-600 text-white'
                : 'bg-white text-emerald-800 hover:bg-emerald-50'}`}
              title="Standard sheet cover width — orderable with initial sheets"
            >Starter</button>
            <button
              type="button"
              onClick={() => setPin({ ...pin, barge_role: 'finish' })}
              data-testid="annot-edit-barge-role-finish"
              className={`px-2 py-0.5 font-semibold border-l border-emerald-300 ${role === 'finish'
                ? 'bg-amber-500 text-white'
                : 'bg-white text-amber-700 hover:bg-amber-50'}`}
              title="Custom measurement — order after sheets installed"
            >Finish</button>
          </div>
        </div>
      )}
      {/* "Order after install" warning banner — appears for Finish
          barges so the office knows this row can't go on the initial
          supplier PO. The Materials Order List groups these separately
          under a PENDING bucket. */}
      {isFinish && (
        <div
          data-testid="annot-edit-barge-finish-warning"
          className="text-[10px] bg-amber-50 ring-1 ring-amber-200 text-amber-800 rounded px-1.5 py-1 leading-tight"
        >
          ⏳ <strong>Finish barge</strong> — measure final width on-site after sheets are installed, then update folds before ordering.
        </div>
      )}

      {/* Compact horizontal fold strip — mirrors Components Flashings UX:
            FOLDS  [1 |25|×]  [2 |20|×]  …  [+ Fold]  [Σ 240mm]  [↻ Reset all]
          Operator types directly into each pill, removes with × button,
          and the live Σ chip at the right shows the total girth. Starter
          barges show the strip read-only (folds locked to template).
          Iter229j — preview SVG sits LEFT of the chip strip (squared up
          + half width) so the dialog stays compact. */}
      <div className="flex items-start gap-1.5" data-testid="annot-edit-valley-preview-row">
        <div className="shrink-0" style={{ width: 125 }}>
          <FoldCrossSectionPreview
            folds={effFolds}
            startAngleDeg={Number(linkedTpl?.start_angle_deg) || 0}
            rotationDeg={previewRot}
            onRotate={onRotate}
          />
        </div>
        <div className="flex-1 min-w-0 self-center">
          {/* Iter229n — top control bar: FOLDS label + [+ Fold] + Σ chip
              on one horizontal line. Fold chips wrap onto the row(s)
              underneath. Keeps the right-hand pane scanable: label →
              add → total, all at-a-glance. */}
          <div className="flex items-center gap-1 mb-1">
            <span
              className="text-[8px] font-bold uppercase tracking-[0.14em] text-emerald-700 shrink-0"
              data-testid="annot-edit-valley-folds-label"
            >Folds{foldsLocked ? ' · locked' : ''}</span>
            {effFolds.length < 20 && !foldsLocked && (
              <button
                type="button"
                data-testid="annot-edit-valley-fold-add"
                onClick={addFold}
                title="Add a fold (defaults to 50mm @ 90°)"
                className="text-[9px] font-bold text-emerald-700 bg-emerald-100 hover:bg-emerald-200 px-1 py-0.5 rounded border border-emerald-200"
              >+ Fold</button>
            )}
            <span
              data-testid="annot-edit-valley-girth-total"
              className="ml-auto inline-flex items-center text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-100 text-emerald-900 border border-emerald-200 font-mono-num"
            >Σ {girth}mm</span>
            {girthDrift && !foldsLocked && (
              <button
                type="button"
                data-testid="annot-edit-valley-girth-reset"
                onClick={resetAll}
                title={`Total girth differs from template (${tplGirth}mm). Click to reset every fold.`}
                className="text-[9px] font-bold text-amber-800 bg-amber-50 hover:bg-amber-100 px-1 py-0.5 rounded border border-amber-200"
              >↻ Reset all</button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-0.5">
            {effFolds.map((f, idx) => {
              const tplLen = Number(tplFolds[idx]?.length_mm) || 0;
              const drift = tplFolds[idx] && Number(f.length_mm) !== tplLen;
              return (
                <div
                  key={idx}
                  className={`inline-flex items-center bg-white border rounded pl-0.5 ${foldsLocked ? 'border-slate-200 opacity-70' : 'border-emerald-200'}`}
                >
                  <span className="text-[8px] font-bold text-emerald-700 mr-0.5">{idx + 1}</span>
                  {/* Iter229n — switched from `type="number"` to a
                      sanitised text input so the native browser
                      spinner arrows are hidden (operators requested
                      "just typed entry"). Still numeric: `inputMode`
                      surfaces the numeric keypad on touch devices,
                      and `onChange` strips non-digits before passing
                      through `updateFoldLen`. */}
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    data-testid={`annot-edit-valley-fold-${idx}`}
                    value={Number(f.length_mm) || 0}
                    disabled={foldsLocked}
                    onChange={(e) => {
                      const cleaned = (e.target.value || '').replace(/[^0-9]/g, '').slice(0, 4);
                      updateFoldLen(idx, cleaned);
                    }}
                    title={foldsLocked
                      ? 'Starter barge — folds locked to template. Switch to Finish role to unlock.'
                      : `Fold ${idx + 1} length (mm). Per-pin edit — master template stays untouched.`}
                    className={`w-7 text-[10px] px-0.5 py-0.5 border-l font-mono-num text-right ${foldsLocked ? 'border-slate-100 bg-slate-50 cursor-not-allowed' : 'border-emerald-100'}`}
                  />
                  {drift && !foldsLocked && (
                    <button
                      type="button"
                      data-testid={`annot-edit-valley-fold-reset-${idx}`}
                      onClick={() => updateFoldLen(idx, String(tplLen))}
                      title={`Fold ${idx + 1} drifted from template (${tplLen}mm). Click to reset.`}
                      className="text-[8px] font-bold text-amber-800 hover:bg-amber-100 px-0.5"
                    >↻</button>
                  )}
                  {!foldsLocked && (
                    <button
                      type="button"
                      data-testid={`annot-edit-valley-fold-remove-${idx}`}
                      onClick={() => removeFold(idx)}
                      disabled={effFolds.length <= 1}
                      title={`Remove fold ${idx + 1}`}
                      className="text-[9px] text-rose-500 hover:bg-rose-50 px-0.5 rounded-r disabled:opacity-30 disabled:cursor-not-allowed"
                    >×</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Save-as-new-template — surfaces ONLY when folds drift from the
          template (otherwise the operator wouldn't be saving anything
          new). Locked Starter barges can't drift, so this is auto-hidden. */}
      {girthDrift && !savePromptOpen && !foldsLocked && (
        <button
          type="button"
          onClick={() => {
            setSavePromptOpen(true);
            setNewTplName(`Cust ${linkedTpl.name} ${summary}`);
          }}
          data-testid="annot-edit-valley-save-as-template"
          className="w-full px-2 py-1 text-[10px] font-semibold text-emerald-800 bg-white hover:bg-emerald-50 ring-1 ring-emerald-400 rounded"
          title="Save these dimensions as a new Valley template (selectable on future pins)"
        >💾 Save as new template</button>
      )}
      {girthDrift && savePromptOpen && (
        <div className="bg-emerald-50 ring-1 ring-emerald-300 rounded p-2 space-y-1.5">
          <label className="block text-[10px] font-semibold text-emerald-800 uppercase tracking-wide">
            Name your custom valley
          </label>
          <input
            type="text"
            data-testid="annot-edit-valley-save-name"
            value={newTplName}
            onChange={(e) => setNewTplName(e.target.value)}
            placeholder='e.g. "Cust 5Fold Valley 25/25/130/130/25/25"'
            className="w-full px-1.5 py-1 text-xs border border-emerald-200 rounded"
            autoFocus
          />
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={saveAsNewTemplate}
              disabled={savingTpl || !newTplName.trim()}
              data-testid="annot-edit-valley-save-confirm"
              className="flex-1 px-2 py-1 text-[11px] font-semibold text-white bg-emerald-700 hover:bg-emerald-800 rounded disabled:opacity-50"
            >{savingTpl ? 'Saving…' : 'Save template'}</button>
            <button
              type="button"
              onClick={() => { setSavePromptOpen(false); setNewTplName(''); }}
              disabled={savingTpl}
              className="px-2 py-1 text-[11px] text-slate-600 underline disabled:opacity-50"
            >Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * FoldCrossSectionPreview — Iter229f / Iter229g
 *
 * Tiny live SVG that visualises a flashing's cross-section from a
 * `folds` array (each `{length_mm, bend_angle, bend_dir}`). Walker
 * starts at the origin facing `startAngleDeg + rotationDeg` and, for
 * each fold:
 *   1. Draw a segment of `length_mm` in the current direction.
 *   2. Rotate by `bend_angle` (cw → subtract, ccw → add).
 *
 * Re-renders on every fold edit so the operator sees the shape change
 * instantly. The `rotationDeg` knob (Iter229g) lets the operator nudge
 * the preview so the cross-section sits the right way up — stored
 * per template via `preview_rotation_deg` so the orientation sticks
 * across pins / appraisals.
 */
export function FoldCrossSectionPreview({ folds, startAngleDeg = 0, rotationDeg = 0, onRotate = null }) {
  // Iter229j — narrowed to ~half width + squared up so the preview can
  // sit side-by-side with the fold-chip strip without dominating the
  // dialog (operator walkthrough on C2536/A5004 feedback).
  // Iter229n — widened ~30% (96→125) so longer V/W valley templates
  // breathe without compressing the diagram into a thin line.
  const W = 125, H = 84, PAD = 5;
  // Walk the folds; apply user rotation up-front so the polyline is
  // computed in the operator's preferred orientation.
  //
  // ⚠ Sign convention MUST match `FlashingProfileSVG` (admin page) +
  // the backend's `_build_fold_polyline`, otherwise the same template
  // renders mirrored / 45° off here vs the admin Flashing Templates
  // library card. Canonical rule: `ccw → heading -=`, `cw → heading +=`.
  // (SVG y-axis points DOWN, so visual ccw == math cw.)
  // Filter zero-length folds out so the polyline matches the admin
  // page byte-for-byte (admin uses `safeFolds`).
  const safeFolds = (Array.isArray(folds) ? folds : []).filter(
    (f) => Number(f?.length_mm) > 0
  );
  if (safeFolds.length === 0) {
    return (
      <div
        className="bg-slate-50 rounded ring-1 ring-slate-200 text-[10px] text-slate-400 italic text-center py-3"
        data-testid="annot-edit-fold-preview-empty"
      >No folds yet — preview will appear here.</div>
    );
  }
  let dirDeg = (Number(startAngleDeg) || 0) + (Number(rotationDeg) || 0);
  let x = 0, y = 0;
  const pts = [[x, y]];
  safeFolds.forEach((f) => {
    const len = Number(f?.length_mm) || 0;
    const rad = (dirDeg * Math.PI) / 180;
    x += len * Math.cos(rad);
    y += len * Math.sin(rad);
    pts.push([x, y]);
    const bend = Number(f?.bend_angle) || 0;
    const sign = (f?.bend_dir === 'ccw') ? -1 : 1;
    dirDeg += sign * bend;
  });
  // Fit-to-viewport scaling.
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const spanX = Math.max(1, maxX - minX);
  const spanY = Math.max(1, maxY - minY);
  // Iter229p — `VISUAL_INSET` reserves room INSIDE the drawing area
  // for the green start-dot, red end-dot (both `r=2`) and the
  // mid-segment number labels (font 6, ~half-width 3). Without it the
  // polyline got fitted edge-to-edge but the decorations spilled past
  // the right-hand edge of the preview box — exactly the "drawing
  // travels past the window right hand side" the operator flagged on
  // the C2536/A5004 walkthrough. The polyline itself is now centred
  // inside an inner box that's `2*VISUAL_INSET` smaller in each axis,
  // so every glyph stays comfortably inside the rounded preview frame.
  const VISUAL_INSET = 4;
  const drawW = W - 2 * PAD - 2 * VISUAL_INSET;
  const drawH = H - 2 * PAD - 2 * VISUAL_INSET;
  const scale = Math.min(drawW / spanX, drawH / spanY);
  const offsetX = PAD + VISUAL_INSET + (drawW - spanX * scale) / 2 - minX * scale;
  const offsetY = PAD + VISUAL_INSET + (drawH - spanY * scale) / 2 - minY * scale;
  const xy = pts.map(([px, py]) => [
    (px * scale + offsetX).toFixed(1),
    (py * scale + offsetY).toFixed(1),
  ]);
  const polyline = xy.map((p) => p.join(',')).join(' ');
  const midpoints = [];
  for (let i = 0; i < xy.length - 1; i++) {
    const [ax, ay] = xy[i];
    const [bx, by] = xy[i + 1];
    midpoints.push([(parseFloat(ax) + parseFloat(bx)) / 2, (parseFloat(ay) + parseFloat(by)) / 2]);
  }
  return (
    <div
      className="bg-slate-50 rounded ring-1 ring-slate-200 px-1 py-1 relative"
      data-testid="annot-edit-fold-preview"
      title="Live cross-section. Re-renders as you edit each fold."
    >
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block mx-auto">
        <circle cx={xy[0][0]} cy={xy[0][1]} r={2} fill="#10b981" />
        <circle cx={xy[xy.length - 1][0]} cy={xy[xy.length - 1][1]} r={2} fill="#f43f5e" />
        <polyline
          points={polyline}
          fill="none"
          stroke="#059669"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          data-testid="annot-edit-fold-preview-polyline"
        />
        {midpoints.map(([mx, my], i) => (
          <text
            key={i}
            x={mx}
            y={my}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="6"
            fontWeight="bold"
            fill="#475569"
            style={{ paintOrder: 'stroke', stroke: '#fff', strokeWidth: 1.8 }}
          >{i + 1}</text>
        ))}
      </svg>
      {/* Iter229g — rotation knob. Persists the operator's preferred
          preview orientation onto the template so every pin sharing
          this template inherits it. Two arrows = 90° increments either
          direction; one quick visual fix per supplier drawing. */}
      {onRotate && (
        <div className="absolute top-0.5 right-0.5 flex gap-0.5">
          <button
            type="button"
            data-testid="annot-edit-fold-preview-rotate-ccw"
            onClick={() => onRotate(((rotationDeg - 90) + 360) % 360)}
            title="Rotate preview 90° anti-clockwise (saved on template)"
            className="w-4 h-4 text-[9px] flex items-center justify-center bg-white hover:bg-slate-100 rounded shadow-sm ring-1 ring-slate-300 text-slate-600 font-bold"
          >↺</button>
          <button
            type="button"
            data-testid="annot-edit-fold-preview-rotate-cw"
            onClick={() => onRotate((rotationDeg + 90) % 360)}
            title="Rotate preview 90° clockwise (saved on template)"
            className="w-4 h-4 text-[9px] flex items-center justify-center bg-white hover:bg-slate-100 rounded shadow-sm ring-1 ring-slate-300 text-slate-600 font-bold"
          >↻</button>
        </div>
      )}
      {rotationDeg !== 0 && (
        <span
          className="absolute bottom-0.5 left-1 text-[8px] text-slate-400 font-mono-num"
          data-testid="annot-edit-fold-preview-rotation-badge"
        >↻ {rotationDeg}°</span>
      )}
    </div>
  );
}
